/**
 * SimpleInput Component
 *
 * Description: This component renders a styled input field or textarea. It provides customization options such as setting the input type, placeholder text, value, and change handler. It supports numeric and text inputs with validation for length and character type.
 *
 * Props:
 * - value (string): The value of the input field. Defaults to an empty string.
 * - onChange (function): A callback function that is called when the value of the input field changes. Defaults to an empty function.
 * - isLabel (boolean): Determines whether a label should be displayed for the input field. Defaults to false.
 * - labelText (string): The text to be displayed in the label if `isLabel` is true. Defaults to an empty string.
 * - isRequired (boolean): Indicates if the input field is required. Adds a red asterisk to the label if true. Defaults to false.
 * - type (string): The type of the input field (e.g., "text", "password"). Defaults to "text".
 * - placeholder (string): The placeholder text displayed in the input field. Defaults to an empty string.
 * - isLimit (number | false): The maximum length of the input value. If set, displays a character count. Defaults to false.
 * - rows (number): The number of rows for textarea input. Defaults to 1.
 * - extraStyle (React.CSSProperties): Additional CSS styles to apply to the input field. Defaults to an empty object.
 * - inputMode ("none" | "text" | "tel" | "url" | "email" | "numeric" | "decimal" | "search"): Defines the input mode for the input field. Defaults to "none".
 * - maxLength (number | undefined): The maximum number of characters allowed in the input field. Defaults to undefined.
 * - minLength (number | undefined): The minimum number of characters required in the input field. Defaults to undefined.
 *
 * File Name: SimpleInput.tsx
 * Date: 24-07-2024
 */

import React, { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { generateDarkenColor } from "src/helpers/helperFunctions";

interface ISimpletInputProps {
  value: string;
  onChange: (value: string) => void; // Corrected type
  isLabel?: boolean;
  labelText?: string;
  isRequired?: boolean;
  type?: "textArea" | "color" | string;
  placeholder?: string;
  isLimit?: number | false;
  rows?: number;
  extraStyle?: React.CSSProperties; // Correct type for CSS styles
  inputMode?:
    | "none"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search"
    | undefined;
  maxLength?: number | undefined;
  minLength?: number | undefined;
  showPasswordEye?: boolean;
  tooglePasswordVisibility?: () => void;
  disabled?: boolean;
  error?: string;
  id?: string;
  newUi?: boolean;
  onEnter?: () => void;
  onKeyDown?: (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  pickerId?: string;
  extraClasses?: string;
  withoutMargin?: boolean;
  inputContainerClasses?: string;
  extraInputContainerClasses?: string;
  [key: string]: any;
}

const SimpleInput: React.FC<ISimpletInputProps> = ({
  value = "",
  onChange = () => {},
  isLabel = false,
  labelText = "",
  isRequired = false,
  type = "text",
  placeholder = "",
  isLimit = false,
  rows = 1,
  extraStyle = {},
  inputMode = "none",
  maxLength = undefined,
  minLength = undefined,
  showPasswordEye = false,
  disabled = false,
  tooglePasswordVisibility = () => {},
  error = "",
  id = "",
  newUI = false,
  onEnter = () => {},
  onKeyDown = () => {},
  pickerId = "",
  extraClasses = "",
  withoutMargin = false,
  inputContainerClasses = "",
  extraInputContainerClasses = "",
  ...props
}) => {
  const [inputType, setInputType] = useState(type);
  // Handle input change and validation
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let newValue = e.target.value;

    // Filter numeric values if inputMode is numeric
    if (inputMode === "numeric") {
      newValue = newValue.replace(/[^0-9]/g, "");
    }

    if (inputMode === "text") {
      newValue = newValue.replace(/[^A-Za-z]/g, "");
    }
    // Apply maxLength and minLength constraints
    if (maxLength && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);
    }
    if (minLength && newValue.length < minLength) {
      // Optionally handle cases where the length is less than minLength
    }

    // Update the value
    onChange(newValue);
  };

  const colorRef = useRef<HTMLInputElement | null>(null);

  let errorMarginTop = withoutMargin
    ? newUI
      ? "lg:-mt-[0.3vw] -mt-2"
      : "lg:-mt-[0.2vw] -mt-1"
    : newUI
    ? "lg:-mt-[0.6vw] -mt-3"
    : "lg:-mt-[0.4vw] -mt-1";

  return (
    <>
      {isLabel && (
        <label
          className={`${
            newUI
              ? "font-Poppins-Medium text-[#2F2F31] font-size-16px"
              : "font-Poppins-Regular font-size-18px"
          }`}
        >
          {isRequired && <span className="text-[#FF0000]">* </span>}
          {labelText}
        </label>
      )}
      {type === "textArea" ? (
        <div
          className={`relative ${
            withoutMargin ? "" : "mb-2"
          } ${inputContainerClasses}`}
        >
          <textarea
            className={`create-role-input font-Poppins-Regular min-h-[100px] mb-0 ${
              newUI
                ? "!border-[#ACACAC] !rounded-[10px] font-size-15px"
                : `font-size-16px ${withoutMargin ? "!mb-0" : "mb-2"}`
            } ${extraClasses}`}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            required={isRequired}
            rows={rows}
            style={extraStyle}
            inputMode={inputMode}
            maxLength={maxLength}
            minLength={minLength}
            disabled={disabled}
            id={id}
            onKeyDown={(e) => {
              onKeyDown?.(e);
              if (
                e.key === "Enter" &&
                !disabled &&
                !e.shiftKey &&
                !e.ctrlKey &&
                !e.altKey
              ) {
                onEnter?.();
              }
            }}
            {...props}
          ></textarea>
          <div
            className={`flex items-center lg:-mt-[0.6vw] -mt-3 mb-1 ${
              error ? "justify-between" : "justify-end"
            } gap-2`}
          >
            {error && (
              <p className={`text-red-500 font-size-14px font-Poppins-Regular`}>
                {error}
              </p>
            )}
            {isLimit && (
              <p
                className={`text-end font-size-14px`}
              >{`${value.length}/${isLimit}`}</p>
            )}
          </div>
        </div>
      ) : type === "color" ? (
        <div
          className={`create-role-input p-[0.2rem] flex justify-between font-Poppins-Regular h-[42px] ${
            disabled ? "opacity-95" : "opacity-100"
          }  ${newUI ? "!border-[#ACACAC] !rounded-[10px]" : ""}`}
        >
          <div>
            {value && (
              <div
                className={`w-[34px] relative cursor-pointer group border h-[34px] rounded`}
                style={{
                  borderColor: value ? generateDarkenColor(value) : "gray",
                  backgroundColor: value ? value : "white",
                }}
              >
                <div
                  className={`w-fit whitespace-nowrap font-size-13px transition-all py-1 px-2  bg-gray-600 text-white rounded opacity-0 group-hover:opacity-100 absolute top-full left-1/2 -translate-x-1/2 group-hover:translate-y-1 group-hover:scale-100 scale-90`}
                >
                  {value}
                </div>
              </div>
            )}
          </div>
          <button
            disabled={disabled}
            id={pickerId}
            className={`bg-[#00443f] relative px-3 !py-[6px] rounded text-[#FFFFFF] font-size-14px font-Poppins-Regular flex items-center justify-center gap-2 w-fit ${
              disabled ? "cursor-default" : "cursor-pointer"
            }`}
            onClick={() => {
              if (colorRef.current) {
                colorRef.current.click();
              }
            }}
          >
            <input
              className="absolute bottom-0 left-0 opacity-0 cursor-pointer"
              type="color"
              id={id || "color-input"}
              ref={colorRef}
              onChange={handleChange}
              onKeyDown={(e) => {
                onKeyDown?.(e);
                if (
                  e.key === "Enter" &&
                  !disabled &&
                  !e.shiftKey &&
                  !e.ctrlKey &&
                  !e.altKey
                ) {
                  onEnter?.();
                }
              }}
            />
            Pick Color
          </button>
        </div>
      ) : (
        <div className={`relative ${inputContainerClasses}`}>
          <input
            className={`create-role-input font-Poppins-Regular ${
              newUI
                ? "!border !border-[#ACACAC] !text-[#303030] 2xl:!rounded-[10px] !rounded-[6px] 2xl:!p-2 !p-1.5 font-size-15px"
                : `font-size-16px ${withoutMargin ? "!mb-0" : "mb-2"}`
            } ${extraClasses}`}
            value={value}
            inputMode={inputMode}
            onChange={handleChange}
            placeholder={placeholder}
            type={type}
            required={isRequired}
            style={extraStyle}
            maxLength={maxLength}
            minLength={minLength}
            disabled={disabled}
            id={id}
            onKeyDown={(e) => {
              onKeyDown?.(e);
              if (
                e.key === "Enter" &&
                !disabled &&
                !e.shiftKey &&
                !e.ctrlKey &&
                !e.altKey
              ) {
                onEnter?.();
              }
            }}
            {...props}
          />
          {showPasswordEye && (
            <div>
              <FontAwesomeIcon
                icon={type === "password" ? faEyeSlash : faEye}
                className="input-field-eye font-size-18px absolute right-4 top-1/2 transform -translate-y-1/2 cursor-pointer"
                onClick={() => tooglePasswordVisibility()}
                id={`toggle-password`}
              />
            </div>
          )}
        </div>
      )}
      {error && type !== "textArea" ? (
        <p
          className={`text-red-500 ${errorMarginTop} mb-1 font-size-14px font-Poppins-Regular`}
        >
          {error}
        </p>
      ) : null}
    </>
  );
};

export default SimpleInput;
